import {noop} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {LabelingStrategy} from 'constants/RegistryConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import * as RegistryUtil from 'registry/RegistryUtil';
import {ValidationErrorData} from 'registry/RegistryValidatorStore';
import CheckableSortableTable from 'ui-library/CheckableSortableTable';
import Clipboard from 'ui-library/Clipboard';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import SortableTable, {SortableTableColumn} from 'ui-library/SortableTable';
import * as ExportUtil from 'util/ExportUtil';

import MergeDuplicatePopover from './MergeDuplicatePopover';
import RegistryValidatorFileCell from './RegistryValidatorFileCell';

export interface RegistryValidatorTableProps {
  addedFileIds: Array<string>;
  disableCheckboxes: boolean;
  errorsByFileId: {[fileId: string]: ValidationErrorData} | null;
  labelingStrategy: LabelingStrategy;
  onChangeChecks: (newCheckedRowIds: Array<string | number>) => void;
  onRequestClose: () => void;
  results: Array<{name: string; id: string; checked: boolean}>;
}

class RegistryValidatorTable extends React.Component<RegistryValidatorTableProps> {
  static defaultProps = {disableCheckboxes: false};

  private renderColumn = (column: SortableTableColumn, row: {data: {[k: string]: string}}): string =>
    row.data[column.accessorKey!];

  private renderErrors = (resultId: string): JSX.Element | null => {
    if (!this.props.errorsByFileId) {
      return null;
    }
    const errorData = RegistryUtil.getErrorData(
      this.props.errorsByFileId[resultId],
      this.props.labelingStrategy
    );
    return (
      <span>
        <div>
          {/* eslint-disable-next-line no-restricted-syntax */}
          {_.map(errorData.warnings, (warning, i) => (
            <div key={`warning-${i}`}>
              <BenchlingIcon icon="warning" />
              <span className="u-tiny-padding-left">{warning}</span>
            </div>
          ))}
        </div>
        <div>
          {/* eslint-disable-next-line no-restricted-syntax */}
          {_.map(errorData.errors, (error, i) => (
            <div key={`error-${i}`}>
              <BenchlingIcon icon="error" />
              <span className="u-tiny-padding-left">{error}</span>
            </div>
          ))}
        </div>
      </span>
    );
  };

  private getTSVErrors = (): string => {
    if (!this.props.errorsByFileId) {
      return '';
    }
    const rows = [];
    rows.push(['Entity', 'Message', 'Status']);
    for (const result of this.props.results) {
      const errorData = RegistryUtil.getErrorData(
        this.props.errorsByFileId[result.id],
        this.props.labelingStrategy
      );
      for (const warning of errorData.warnings) {
        rows.push([result.name, warning, 'Warning']);
      }
      for (const error of errorData.errors) {
        rows.push([result.name, error, 'Error']);
      }
    }
    return ExportUtil.formatRows(rows, '\t');
  };

  private renderErrorHeader = (): JSX.Element => (
    <div className="u-lr-split">
      <div>Errors</div>
      <Clipboard value={this.getTSVErrors()}>
        <a>Copy errors</a>
      </Clipboard>
    </div>
  );

  private renderFooter = (): JSX.Element => {
    const hasDuplicationWarning = Object.values(this.props.errorsByFileId!).some(
      ({warningCode}) => warningCode && warningCode.includes('DUPLICATE_ERROR')
    );

    return (
      <div>
        {hasDuplicationWarning && (
          <MergeDuplicatePopover>
            <a>What happens when my duplicate entities get merged?</a>
          </MergeDuplicatePopover>
        )}
      </div>
    );
  };

  override render(): JSX.Element {
    assert(this.props.results.length > 0, 'Cannot render RegistryValidatorTable with empty results');
    const columns = [
      SortableTable.defaultColumn('Entity', 'file', {render: this.renderColumn, sortable: false}),
      SortableTable.defaultColumn(this.renderErrorHeader(), 'errors', {
        render: this.renderColumn,
        sortable: false,
      }),
    ];

    const rows = this.props.results.map((result) => ({
      data: {
        file: (
          <RegistryValidatorFileCell
            fileId={result.id}
            isLinkedFile={this.props.addedFileIds.includes(result.id)}
            name={result.name}
            onFileClick={this.props.onRequestClose}
          />
        ),
        errors: this.renderErrors(result.id),
      },
      id: result.id,
      // TODO(T5188): Use hovericons here to allow removing single files.
      icon: (
        <BenchlingIcon className="scrollableTable-cell-icon" icon={FileBrowserUtil.getFileIconName(result)} />
      ),
      onClick: noop,
      onDoubleClick: noop,
      checked: result.checked,
    }));

    return (
      <div>
        <CheckableSortableTable
          disableCheckboxes={this.props.disableCheckboxes}
          columns={columns}
          rows={rows}
          onChangeChecks={this.props.onChangeChecks}
        />
        {this.renderFooter()}
      </div>
    );
  }
}

export default RegistryValidatorTable;
